import React, { useEffect, useState } from 'react';
import { evictionService } from '../../services/api';

interface CaseListProps {
    onSelectCase: (caseData: any) => void;
    onNewCase: () => void;
}

export default function CaseList({ onSelectCase, onNewCase }: CaseListProps) {
    const [cases, setCases] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchCases = async () => {
        try {
            const res = await evictionService.getCases();
            setCases(res.data.data || []);
        } catch (err: any) {
            console.error(err);
            setError('Failed to load eviction cases.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCases();
    }, []);

    const getStatusBadge = (status: string) => {
        if (!status) return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">New</span>;

        const styles = {
            draft: 'bg-gray-100 text-gray-800',
            notice_served: 'bg-yellow-100 text-yellow-800',
            court: 'bg-orange-100 text-orange-800',
            completed: 'bg-green-100 text-green-800'
        };
        const style = styles[status as keyof typeof styles] || styles.draft;
        return (
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${style}`}>
                {(status || 'draft').replace(/_/g, ' ')}
            </span>
        );
    };

    if (loading) return <div className="text-center py-4">Loading cases...</div>;
    if (error) return <div className="text-center py-4 text-red-600">{error}</div>;

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Eviction Cases</h3>
                <button
                    onClick={onNewCase}
                    className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                    <i className="fas fa-plus mr-2"></i> Start New Case
                </button>
            </div>

            {cases.length === 0 ? (
                <div className="text-center py-10">
                    <i className="fas fa-folder-open text-gray-400 text-4xl mb-3"></i>
                    <p className="text-gray-500">No eviction cases found.</p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-200">
                    {cases.filter(Boolean).map((evictionCase) => (
                        <li key={evictionCase.id}>
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); onSelectCase(evictionCase); }}
                                className="block hover:bg-gray-50"
                            >
                                <div className="px-4 py-4 sm:px-6">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-indigo-600 truncate">
                                            {typeof evictionCase.tenantName === 'object' ? JSON.stringify(evictionCase.tenantName) : (evictionCase.tenantName || 'Unknown Tenant')}
                                        </p>
                                        <div className="ml-2 flex-shrink-0 flex">
                                            {getStatusBadge(typeof evictionCase.status === 'object' ? 'draft' : evictionCase.status)}
                                        </div>
                                    </div>
                                    <div className="mt-2 sm:flex sm:justify-between">
                                        <div className="sm:flex">
                                            <p className="flex items-center text-sm text-gray-500">
                                                <i className="fas fa-home flex-shrink-0 mr-1.5 text-gray-400"></i>
                                                {typeof evictionCase.propertyAddress === 'object' ? 'Property' : (evictionCase.propertyAddress || 'Property Address')}
                                            </p>
                                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                                <i className="fas fa-gavel flex-shrink-0 mr-1.5 text-gray-400"></i>
                                                Ground {typeof evictionCase.groundCode === 'object' ? (evictionCase.groundCode.code || evictionCase.groundCode.name) : evictionCase.groundCode}
                                            </p>
                                        </div>
                                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                            <i className="fas fa-calendar flex-shrink-0 mr-1.5 text-gray-400"></i>
                                            <p>
                                                Created on <time dateTime={evictionCase.createdAt}>{new Date(evictionCase.createdAt).toLocaleDateString()}</time>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
